// BulkInviteTeamPage.jsx
//
// Multi-row team invite — replaces the click-add-one-at-a-time flow when a
// firm is onboarding (or any time they want to add several people at once).
//
// Each row captures: first name, last name, email, role, access mode.
//
// Three access modes per row:
//   "full"     → calls inviteTeamMember()  → auth.users + employees row + invite email
//   "profile"  → calls addStaffRecord()    → employees row only, no login
//   "pending"  → calls addStaffRecord()    → employees row with status='inactive', no email yet
//
// Smart defaults by role:
//   Partner / Associate / Of Counsel / Administrator → full
//   Paralegal / Legal Assistant / Legal Secretary    → full   (they usually need access)
//   Billing Clerk                                    → profile
//
// Bulk-paste field: accepts tab / comma / pipe-separated rows from a spreadsheet:
//   First<TAB>Last<TAB>email<TAB>Role
// fills the table; anything unparsable is dropped.
//
// Each row is processed independently. Partial failures are surfaced in a
// per-row status column (success / failed / pending) with the underlying error.

import React, { useMemo, useState } from 'react';
import {
  UserPlus, Users, ClipboardPaste, Trash2, Plus, Send, KeyRound, ShieldOff,
  Clock, Check, AlertCircle, Loader, ChevronLeft, Sparkles, Mail,
} from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Input, Select, Badge, Alert, Modal,
} from '../../components/ui';
import { useFirm } from '../../contexts/FirmContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useRoute } from '../../lib/router';
import { inviteTeamMember, addStaffRecord } from '../../lib/db';
import { EMPLOYEE_ROLES } from '../../lib/constants';
import { classNames } from '../../lib/formatters';
import toast from '../../lib/toast';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Smart-default access mode per role. Must include every entry in
// EMPLOYEE_ROLES from src/lib/constants.js (otherwise the bulk-invite
// UI falls back to 'full' for unknown roles — see the
// `DEFAULT_ACCESS_BY_ROLE[finalRole] || 'full'` line below).
//
// Roles that NEED to sign in (attorneys, paralegals, anyone touching
// matters) → 'full'. Roles that may not have an email or shouldn't
// have a login by default (Billing Clerk doing data entry from a
// shared computer) → 'profile' (visible in directory, no login).
const DEFAULT_ACCESS_BY_ROLE = {
  // Permission-bearing system roles (migration 031)
  'Admin':           'full',
  'Partner':         'full',
  'Attorney':        'full',
  'Associate':       'full',
  'Paralegal':       'full',
  'Clerk':           'full',
  'IT':              'full',    // IT needs login to do its job (Settings + team mgmt)
  // Descriptive / legacy text-only roles
  'Of Counsel':      'full',
  'Administrator':   'full',
  'Legal Assistant': 'full',
  'Legal Secretary': 'full',
  'Billing Clerk':   'profile', // typically a non-login role at small firms
};

const ACCESS_MODES = [
  { value: 'full',    label: 'Full login access', desc: 'Receives an invite email and can sign in to the app',           icon: KeyRound,  tone: 'indigo' },
  { value: 'profile', label: 'Profile only',      desc: 'Visible in the directory and assignable, but no login',         icon: ShieldOff, tone: 'slate' },
  { value: 'pending', label: 'Draft (no invite)', desc: 'Saved for later — no email sent yet, status stays inactive',    icon: Clock,     tone: 'amber' },
];

const blankRow = () => ({
  id: crypto.randomUUID(),
  firstName: '',
  lastName:  '',
  email:     '',
  role:      'Associate',
  access:    DEFAULT_ACCESS_BY_ROLE['Associate'],
  // submit-result state
  status:    'idle',          // idle | submitting | success | failed
  error:     null,
});

// Parse one line of pasted text into a row. Splits on TAB first, then comma,
// then 2+ spaces, then pipe. Tries to detect which column is the email and
// which is the role (anything matching EMAIL_RE → email; anything matching
// a known role → role).
function parseLine(line) {
  if (!line.trim()) return null;
  const tokens = line.split(/\t|,|\s{2,}|\|/).map((t) => t.trim()).filter(Boolean);
  if (tokens.length === 0) return null;

  let email = '';
  let role  = '';
  const nameParts = [];

  for (const tok of tokens) {
    if (!email && EMAIL_RE.test(tok)) { email = tok; continue; }
    if (!role && EMPLOYEE_ROLES.some((r) => r.toLowerCase() === tok.toLowerCase())) {
      role = EMPLOYEE_ROLES.find((r) => r.toLowerCase() === tok.toLowerCase());
      continue;
    }
    nameParts.push(tok);
  }

  // Split the leftover name tokens: first token = first name, rest = last name
  // Single token: treat as first name.
  const firstName = nameParts[0] || '';
  const lastName  = nameParts.slice(1).join(' ');

  if (!firstName && !email) return null;
  const finalRole = role || 'Associate';
  return {
    ...blankRow(),
    firstName,
    lastName,
    email,
    role:   finalRole,
    access: DEFAULT_ACCESS_BY_ROLE[finalRole] || 'full',
  };
}


export default function BulkInviteTeamPage() {
  const { firm, firmId, refresh: refreshFirm } = useFirm();
  const { can }          = usePermissions();
  const [, navigate]     = useRoute();
  const canManage        = can ? can('team.manage') : true;

  const [rows, setRows] = useState(() => Array.from({ length: 4 }, blankRow));
  const [pasteOpen, setPasteOpen]   = useState(false);
  const [pasteText, setPasteText]   = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done,       setDone]       = useState(false);

  const setRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const addRow    = ()   => setRows((rs) => [...rs, blankRow()]);
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  const onRoleChange = (id, newRole) => {
    const row = rows.find((r) => r.id === id);
    // If access was still at the previous role's smart default, follow the
    // new role's smart default. If the user explicitly overrode access,
    // respect their choice.
    const previousDefault = DEFAULT_ACCESS_BY_ROLE[row?.role] || 'full';
    const shouldFollowDefault = row?.access === previousDefault;
    const patch = { role: newRole };
    if (shouldFollowDefault) patch.access = DEFAULT_ACCESS_BY_ROLE[newRole] || 'full';
    setRow(id, patch);
  };

  // ── Bulk paste handler ────────────────────────────────────────────────────
  const applyPaste = () => {
    const parsed = pasteText
      .split(/\r?\n/)
      .map(parseLine)
      .filter(Boolean);
    if (parsed.length === 0) {
      toast.error('Could not parse any rows. Make sure each line has at least a name or email.');
      return;
    }
    // Replace empty rows first, then append
    setRows((existing) => {
      const filled = existing.filter((r) => r.firstName || r.lastName || r.email);
      return [...filled, ...parsed];
    });
    setPasteText('');
    setPasteOpen(false);
    toast.success(`${parsed.length} row${parsed.length === 1 ? '' : 's'} added from paste.`);
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const rowErrors = useMemo(() => {
    const errs = {};
    const seenEmails = new Set();
    for (const r of rows) {
      const hasAny = r.firstName.trim() || r.lastName.trim() || r.email.trim();
      if (!hasAny) continue;          // empty row → not submitted
      const e = [];
      if (!r.firstName.trim()) e.push('First name required');
      if (r.access === 'full') {
        if (!r.email.trim())                 e.push('Email required for login access');
        else if (!EMAIL_RE.test(r.email))    e.push('Invalid email');
      } else if (r.email && !EMAIL_RE.test(r.email)) {
        e.push('Invalid email');
      }
      if (r.email) {
        const key = r.email.trim().toLowerCase();
        if (seenEmails.has(key)) e.push('Duplicate email in this batch');
        seenEmails.add(key);
      }
      if (e.length) errs[r.id] = e;
    }
    return errs;
  }, [rows]);

  const populatedRows = rows.filter((r) => r.firstName.trim() || r.lastName.trim() || r.email.trim());
  const hasErrors    = Object.keys(rowErrors).length > 0;

  const summary = useMemo(() => {
    const result = { full: 0, profile: 0, pending: 0 };
    for (const r of populatedRows) {
      if (rowErrors[r.id]) continue;
      result[r.access] = (result[r.access] || 0) + 1;
    }
    return result;
  }, [populatedRows, rowErrors]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const submitAll = async () => {
    setSubmitting(true);
    const toSubmit = populatedRows.filter((r) => !rowErrors[r.id]);
    for (const row of toSubmit) {
      setRow(row.id, { status: 'submitting', error: null });
      try {
        if (row.access === 'full') {
          const { error } = await inviteTeamMember(firmId, {
            email:     row.email.trim(),
            firstName: row.firstName.trim(),
            lastName:  row.lastName.trim(),
            role:      row.role,
          });
          if (error) throw error;
        } else {
          // profile or pending — both go through addStaffRecord
          const { error } = await addStaffRecord(firmId, {
            firstName: row.firstName.trim(),
            lastName:  row.lastName.trim(),
            email:     row.email.trim(),
            role:      row.role,
          });
          if (error) throw error;
        }
        setRow(row.id, { status: 'success', error: null });
      } catch (err) {
        setRow(row.id, { status: 'failed', error: err?.message || String(err) });
      }
    }
    setSubmitting(false);
    setConfirming(false);
    setDone(true);

    // Bump the firm-wide refresh signal so any cached list (Employees,
    // Settings → Team, etc.) re-fetches and shows the new rows when the
    // user navigates there.
    try { await refreshFirm?.(true); } catch { /* best-effort */ }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (!canManage) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Alert tone="warning" title="Permission required">
          You need the <strong>team.manage</strong> permission (Partner or Administrator role)
          to invite teammates.
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/settings?section=team')}
            className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mt-1"
            aria-label="Back to employees"
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2 text-slate-900 dark:text-white">
              <Users size={22} className="text-indigo-600" /> Invite your team
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Add multiple people to {firm?.name || 'your firm'} at once. Each row can either get a login or be saved as a directory entry only.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={ClipboardPaste} onClick={() => setPasteOpen(true)}>
            Paste from spreadsheet
          </Button>
        </div>
      </div>

      {/* Access-mode legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {ACCESS_MODES.map(({ value, label, desc, icon: Icon, tone }) => (
          <div key={value} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon size={14} className="text-indigo-600 dark:text-indigo-400" />
              <span className="font-medium text-sm text-slate-900 dark:text-white">{label}</span>
              <Badge tone={tone}>{value}</Badge>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">{desc}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardHeader
          title="Team members"
          subtitle={`${populatedRows.length} of ${rows.length} rows filled`}
          action={
            <Button variant="ghost" icon={Plus} size="sm" onClick={addRow}>Add row</Button>
          }
        />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="text-left px-4 py-2 font-medium w-[18%]">First name</th>
                  <th className="text-left px-4 py-2 font-medium w-[18%]">Last name</th>
                  <th className="text-left px-4 py-2 font-medium w-[24%]">Email</th>
                  <th className="text-left px-4 py-2 font-medium w-[14%]">Role</th>
                  <th className="text-left px-4 py-2 font-medium w-[18%]">Access</th>
                  <th className="text-right px-4 py-2 font-medium w-[8%]">Status</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const errs = rowErrors[row.id] || [];
                  return (
                    <tr
                      key={row.id}
                      className={classNames(
                        'border-t border-slate-100 dark:border-slate-800 align-top',
                        idx % 2 === 1 && 'bg-slate-50/50 dark:bg-slate-800/20',
                        row.status === 'success' && '!bg-emerald-50/60 dark:!bg-emerald-900/15',
                        row.status === 'failed'  && '!bg-red-50/60 dark:!bg-red-900/15',
                      )}
                    >
                      <td className="px-3 py-2">
                        <Input
                          value={row.firstName}
                          onChange={(e) => setRow(row.id, { firstName: e.target.value })}
                          placeholder="Ama"
                          disabled={row.status === 'success'}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={row.lastName}
                          onChange={(e) => setRow(row.id, { lastName: e.target.value })}
                          placeholder="Mensah"
                          disabled={row.status === 'success'}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="email"
                          value={row.email}
                          onChange={(e) => setRow(row.id, { email: e.target.value })}
                          placeholder="ama@firm.com"
                          disabled={row.status === 'success'}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={row.role}
                          onChange={(e) => onRoleChange(row.id, e.target.value)}
                          options={EMPLOYEE_ROLES}
                          disabled={row.status === 'success'}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={row.access}
                          onChange={(e) => setRow(row.id, { access: e.target.value })}
                          options={ACCESS_MODES.map((m) => ({ value: m.value, label: m.label }))}
                          disabled={row.status === 'success'}
                        />
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {row.status === 'submitting' && <Loader size={14} className="animate-spin text-indigo-500 inline" />}
                        {row.status === 'success'    && <Badge tone="green"><Check size={11} className="-ml-0.5 mr-0.5" />Invited</Badge>}
                        {row.status === 'failed'     && <Badge tone="red"><AlertCircle size={11} className="-ml-0.5 mr-0.5" />Failed</Badge>}
                        {row.status === 'idle' && errs.length > 0 && (
                          <span title={errs.join('. ')} className="text-xs text-red-600 dark:text-red-400 inline-flex items-center gap-1">
                            <AlertCircle size={12} /> {errs.length}
                          </span>
                        )}
                      </td>
                      <td className="px-1 py-2 text-right">
                        <button
                          onClick={() => removeRow(row.id)}
                          disabled={row.status === 'success' || rows.length === 1}
                          className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Remove row"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Per-row error list (below the table, only shown if any) */}
          {Object.keys(rowErrors).length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 bg-amber-50/60 dark:bg-amber-900/15 text-xs text-amber-800 dark:text-amber-200">
              <strong>Fix these before sending:</strong>
              <ul className="mt-1 space-y-0.5 list-disc pl-5">
                {populatedRows.map((r) => rowErrors[r.id] && (
                  <li key={r.id}>
                    Row for <em>{r.firstName || r.email || 'unnamed'}</em>: {rowErrors[r.id].join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Failure details (after submit) */}
          {done && rows.some((r) => r.status === 'failed') && (
            <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 bg-red-50/60 dark:bg-red-900/15 text-xs text-red-800 dark:text-red-200">
              <strong>Failures from this batch:</strong>
              <ul className="mt-1 space-y-0.5 list-disc pl-5">
                {rows.filter((r) => r.status === 'failed').map((r) => (
                  <li key={r.id}>
                    <em>{r.firstName} {r.lastName}</em> ({r.email}): {r.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Bottom action bar */}
      <div className="sticky bottom-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-600 dark:text-slate-300">
          {populatedRows.length === 0 ? (
            <span className="text-slate-400">Fill in at least one row to get started.</span>
          ) : (
            <>
              <strong>{populatedRows.length - Object.keys(rowErrors).length}</strong> ready ·{' '}
              <span className="text-indigo-600 dark:text-indigo-400">{summary.full || 0}</span> login,{' '}
              <span className="text-slate-500">{summary.profile || 0}</span> profile-only,{' '}
              <span className="text-amber-600 dark:text-amber-400">{summary.pending || 0}</span> draft
            </>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => navigate('/settings?section=team')}>Cancel</Button>
          <Button
            icon={done ? Check : Send}
            disabled={populatedRows.length === 0 || hasErrors || submitting}
            loading={submitting}
            onClick={() => setConfirming(true)}
          >
            {done ? 'Done' : `Send ${populatedRows.length - Object.keys(rowErrors).length} invite${populatedRows.length - Object.keys(rowErrors).length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>

      {/* Paste modal */}
      <Modal
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        title="Paste from a spreadsheet"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPasteOpen(false)}>Cancel</Button>
            <Button icon={ClipboardPaste} onClick={applyPaste} disabled={!pasteText.trim()}>
              Add {pasteText.split(/\r?\n/).filter((l) => l.trim()).length || 0} row{pasteText.split(/\r?\n/).filter((l) => l.trim()).length === 1 ? '' : 's'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Paste rows from Excel, Google Sheets or a CSV. We try to detect first name, last name, email and role automatically. One person per line:
          </p>
          <pre className="text-xs bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 overflow-x-auto">
{'Ama\tMensah\tama@firm.com\tAssociate\nKofi\tAddo\tkofi@firm.com\tParalegal\nAkua\t \t \tLegal Secretary'}
          </pre>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={10}
            placeholder="Paste your rows here…"
            className="block w-full rounded-lg border-0 bg-white px-3 py-2 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700 font-mono"
          />
        </div>
      </Modal>

      {/* Confirm modal */}
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Send invites?"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={submitting}>Cancel</Button>
            <Button icon={Send} loading={submitting} onClick={submitAll}>
              Yes, send {populatedRows.length - Object.keys(rowErrors).length}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
          <p>You're about to add <strong>{populatedRows.length - Object.keys(rowErrors).length}</strong> team member{populatedRows.length - Object.keys(rowErrors).length === 1 ? '' : 's'}:</p>
          <ul className="space-y-1.5">
            {summary.full > 0 && (
              <li className="flex items-center gap-2">
                <KeyRound size={14} className="text-indigo-600 dark:text-indigo-400" />
                <span><strong>{summary.full}</strong> will receive a login invite email</span>
              </li>
            )}
            {summary.profile > 0 && (
              <li className="flex items-center gap-2">
                <ShieldOff size={14} className="text-slate-500" />
                <span><strong>{summary.profile}</strong> will be added to the directory (no login)</span>
              </li>
            )}
            {summary.pending > 0 && (
              <li className="flex items-center gap-2">
                <Clock size={14} className="text-amber-500" />
                <span><strong>{summary.pending}</strong> will be saved as drafts (no email sent)</span>
              </li>
            )}
          </ul>
          <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-800">
            Each row is processed independently. If one fails the others still go through; you'll see per-row results in the table after this completes.
          </p>
        </div>
      </Modal>

      {/* Success splash after all done (no failures) */}
      {done && !rows.some((r) => r.status === 'failed') && (
        <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/10 border border-emerald-200 dark:border-emerald-700 p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">All set!</h3>
            <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-0.5">
              {summary.full > 0 && <><Mail size={12} className="inline mr-1" />Invite emails are on their way. </>}
              {summary.profile > 0 || summary.pending > 0 ? (
                <>Profile-only entries (no login) appear in the directory under <strong>Staff records — no system access</strong>.</>
              ) : (
                <>You can manage the team on the Employees page.</>
              )}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button icon={UserPlus} variant="primary" size="sm" onClick={() => navigate('/employees')}>
                View directory
              </Button>
              <Button variant="secondary" size="sm" onClick={() => navigate('/settings?section=team')}>
                Manage in Settings
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setRows(Array.from({ length: 4 }, blankRow)); setDone(false); }}>
                Invite another batch
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
